"use client";

import type * as React from "react";
import { LucideX, XIcon } from "lucide-react";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

type DrawerDirection = "top" | "right" | "bottom" | "left";

export type AppDrawerProps = {
  trigger: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  direction?: DrawerDirection;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  contentClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  /** Toolbar header: close (left), title (center), clear action (right) */
  headerMode?: "default" | "toolbar" | "minimal";
  hideHeader?: boolean;
  onClear?: () => void;
  clearLabel?: string;
  /** When false, outside clicks and escape won't close the drawer. */
  dismissible?: boolean;
  /** When false, allows interaction with portaled popovers outside the drawer. */
  modal?: boolean;
};

export function AppDrawer({
  trigger,
  title,
  description,
  children,
  footer,
  direction = "right",
  open,
  onOpenChange,
  contentClassName,
  headerClassName,
  bodyClassName,
  headerMode = "default",
  hideHeader = false,
  onClear,
  clearLabel = "Clear",
  dismissible = true,
  modal = true,
}: AppDrawerProps) {
  return (
    <Drawer
      direction={direction}
      open={open}
      onOpenChange={onOpenChange}
      dismissible={dismissible}
      modal={modal}
    >
      <DrawerTrigger asChild>{trigger}</DrawerTrigger>
      <DrawerContent
        className={cn(
          "flex max-h-[100dvh] flex-col",
          direction === "right" &&
            "data-[vaul-drawer-direction=right]:sm:max-w-xl",
          direction === "left" &&
            "data-[vaul-drawer-direction=left]:sm:max-w-xl",
          contentClassName
        )}
      >
        {!hideHeader && headerMode === "toolbar" ? (
          <div
            className={cn(
              "flex shrink-0 items-center justify-between border-b border-border px-4 py-3",
              headerClassName
            )}
          >
            <DrawerClose
              className="inline-flex size-9 cursor-pointer items-center justify-center rounded-md text-gray-900 transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <XIcon className="size-5" />
            </DrawerClose>
            <DrawerTitle className="text-base font-bold">{title}</DrawerTitle>
            <button
              type="button"
              onClick={onClear}
              className="cursor-pointer text-sm font-medium text-gray-900 hover:underline"
            >
              {clearLabel}
            </button>
          </div>
        ) : null}

        {!hideHeader && headerMode === "minimal" ? (
          <>
            <DrawerTitle className="sr-only">
              {title || "Drawer"}
            </DrawerTitle>
            <div
              className={cn(
                "flex justify-end px-8 pt-2",
                headerClassName
              )}
            >
              <DrawerClose
                className="inline-flex size-9 cursor-pointer items-center justify-center rounded-md text-gray-900 transition-colors hover:bg-gray-100"
                aria-label="Close"
              >
                <LucideX size={24}/>
              </DrawerClose>
            </div>
          </>
        ) : null}

        {!hideHeader && headerMode === "default" ? (
          <DrawerHeader className={cn("border-b border-border", headerClassName)}>
            <DrawerTitle>{title}</DrawerTitle>
            {description ? (
              <DrawerDescription>{description}</DrawerDescription>
            ) : null}
          </DrawerHeader>
        ) : null}

        <div
          className={cn(
            "min-h-0 flex-1 overflow-y-auto px-4 py-6",
            bodyClassName
          )}
        >
          {children}
        </div>

        {footer ? (
          <DrawerFooter className="shrink-0 border-t border-border p-4">
            {footer}
          </DrawerFooter>
        ) : null}
      </DrawerContent>
    </Drawer>
  );
}

export { DrawerClose };
